/**
 * DeleteAccountModal - 2-step account deletion confirmation
 * Canvas Sage / Liquid Glass aesthetic. Inline style tokens only.
 */

import { useState } from 'react';
import { useAuth } from '@/contexts';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X, Download } from 'lucide-react';
import { T } from '@/styles/tokens';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExportFirst: () => void;
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 70,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
  background: 'rgba(0,0,0,0.35)',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
};

const card: React.CSSProperties = {
  width: '100%',
  maxWidth: 360,
  background: T.white,
  borderRadius: T.radiusLg,
  boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
  overflow: 'hidden',
  fontFamily: T.fontFamily,
  animation: 'deleteModalIn 0.28s cubic-bezier(0.16, 1, 0.3, 1)',
};

const iconCircle: React.CSSProperties = {
  width: 48,
  height: 48,
  borderRadius: '50%',
  background: 'rgba(214,69,69,0.08)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px',
};

const title: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 700,
  color: T.text,
  textAlign: 'center',
  letterSpacing: '-0.02em',
  margin: 0,
};

const body: React.CSSProperties = {
  fontSize: 13,
  color: T.textSecondary,
  textAlign: 'center',
  lineHeight: 1.45,
  margin: '6px 0 0',
};

const consequenceRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  fontSize: 13,
  color: T.text,
  lineHeight: 1.4,
};

const consequenceIcon: React.CSSProperties = {
  width: 16,
  height: 16,
  flexShrink: 0,
};

const btnBase: React.CSSProperties = {
  flex: 1,
  padding: '14px 0',
  fontSize: 15,
  fontWeight: 600,
  fontFamily: T.fontFamily,
  border: 'none',
  cursor: 'pointer',
  transition: 'background 0.15s ease',
};

export function DeleteAccountModal({ isOpen, onClose, onExportFirst }: DeleteAccountModalProps) {
  const { deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'warning' | 'confirm'>('warning');

  const handleClose = () => {
    if (isDeleting) return;
    onClose();
    // Reset after close animation
    setTimeout(() => {
      setStep('warning');
      setConfirmText('');
      setError(null);
    }, 200);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isDeleting) handleClose();
  };

  const handleDelete = async () => {
    if (confirmText.toLowerCase() !== 'delete') return;
    setIsDeleting(true);
    setError(null);
    try {
      await deleteAccount();
      navigate('/signup');
    } catch (err: any) {
      console.error('Delete account error:', err);
      setError(err.message || 'Unable to delete account. Try again.');
      setIsDeleting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && confirmText.toLowerCase() === 'delete' && !isDeleting) {
      handleDelete();
    }
    if (e.key === 'Escape' && !isDeleting) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div style={overlay} onClick={handleBackdropClick}>
      <div style={card}>
        {step === 'warning' ? (
          <>
            <div style={{ padding: '28px 24px 20px' }}>
              <div style={iconCircle}>
                <AlertTriangle size={22} color={T.danger} />
              </div>
              <h2 style={title}>Delete account</h2>
              <p style={body}>This cannot be undone.</p>

              {/* Consequences */}
              <div style={{
                background: T.cream,
                borderRadius: T.radiusMd,
                padding: 16,
                marginTop: 20,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}>
                {[
                  'Delete your profile and email',
                  'Anonymize your verdict history',
                  'Cancel any active subscription',
                ].map((text) => (
                  <div key={text} style={consequenceRow}>
                    <X size={14} color={T.danger} style={consequenceIcon} />
                    <span>{text}</span>
                  </div>
                ))}
              </div>

              {/* Export escape hatch */}
              <button
                onClick={onExportFirst}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '12px 0',
                  marginTop: 16,
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: T.fontFamily,
                  color: T.primary,
                  background: T.primarySubtle,
                  border: 'none',
                  borderRadius: T.radiusMd,
                  cursor: 'pointer',
                  transition: 'background 0.15s ease',
                }}
              >
                <Download size={15} />
                Export data first
              </button>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', borderTop: `1px solid ${T.borderLight}` }}>
              <button
                onClick={handleClose}
                style={{ ...btnBase, color: T.textSecondary, background: T.white }}
              >
                Cancel
              </button>
              <div style={{ width: 1, background: T.borderLight }} />
              <button
                onClick={() => { setStep('confirm'); setError(null); }}
                style={{ ...btnBase, color: T.danger, background: T.white }}
              >
                Continue
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ padding: '28px 24px 20px' }}>
              <h2 style={title}>Confirm deletion</h2>
              <p style={{ ...body, margin: '8px 0 20px' }}>
                Type <span style={{ fontWeight: 600, color: T.danger }}>delete</span> to permanently remove your account.
              </p>

              <input
                type="text"
                value={confirmText}
                onChange={(e) => { setConfirmText(e.target.value); setError(null); }}
                onKeyDown={handleKeyDown}
                placeholder="delete"
                autoFocus
                autoComplete="off"
                disabled={isDeleting}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: 15,
                  fontFamily: T.fontFamily,
                  color: T.text,
                  background: T.cream,
                  border: `1px solid ${error ? T.danger : T.border}`,
                  borderRadius: T.radiusMd,
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.15s ease',
                }}
                onFocus={(e) => {
                  if (!error) e.currentTarget.style.borderColor = T.primary;
                }}
                onBlur={(e) => {
                  if (!error) e.currentTarget.style.borderColor = T.border;
                }}
              />

              {error && (
                <p style={{ fontSize: 12, color: T.danger, textAlign: 'center', marginTop: 8 }}>
                  {error}
                </p>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', borderTop: `1px solid ${T.borderLight}` }}>
              <button
                onClick={() => { setStep('warning'); setConfirmText(''); setError(null); }}
                disabled={isDeleting}
                style={{
                  ...btnBase,
                  color: T.textSecondary,
                  background: T.white,
                  opacity: isDeleting ? 0.5 : 1,
                }}
              >
                Back
              </button>
              <div style={{ width: 1, background: T.borderLight }} />
              <button
                onClick={handleDelete}
                disabled={confirmText.toLowerCase() !== 'delete' || isDeleting}
                style={{
                  ...btnBase,
                  color: confirmText.toLowerCase() === 'delete' ? T.white : T.textMuted,
                  background: confirmText.toLowerCase() === 'delete' ? T.danger : T.borderLight,
                  cursor: confirmText.toLowerCase() === 'delete' && !isDeleting ? 'pointer' : 'not-allowed',
                  opacity: isDeleting ? 0.8 : 1,
                }}
              >
                {isDeleting ? 'Deleting...' : 'Delete permanently'}
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes deleteModalIn {
          from { transform: scale(0.96); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default DeleteAccountModal;
